import { AuthContext, AuthContextType } from "@/features/app-layout/context";
import { axiosReq, registerTokenHandlers, setupAxiosInterceptors } from "@/lib/axios";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import posthog from "posthog-js";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const useAxiosAuth = () => {
  const { token, onLogin, username, onLogout, name } = useContext(AuthContext);
  
  useEffect(() => {
    // Setup request interceptor with current token
    const requestInterceptor = setupAxiosInterceptors(() => token);
    
    // Register token updater for response interceptor
    registerTokenHandlers(
      // Update token handler
      (newToken: string, newUsername: string, newName: string) => {
        console.log("token passed to onLogin");
        onLogin(newUsername, newToken, newName);
      },
      // Logout handler
      () => {
        onLogout();
      }
    );
    
    // Cleanup function
    return () => {
      if (requestInterceptor) {
        axiosReq.interceptors.request.eject(requestInterceptor);
      }
    };
  }, [token, onLogin, username, onLogout, name]);
};

export const useAuth = () => {

  const navigate = useNavigate()

  const { onLogin, isPlayground } = useContext<AuthContextType>(AuthContext)

  const request =
    async ({ username, password }: { username: string, password: string }): Promise<{
      access_token: string, name: string
    }> => {
      const response = await axiosReq.post('/api/landing/login/', {
        username, password
      })

      return response.data
    }

  const { mutate: loginUser, status, error } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {

      posthog.identify(
        data.name,
        { username: data.name }
      )

      if (data) {
        onLogin(data.name, data.access_token, data.name);
        if (isPlayground) {
          navigate('/home/play')
        } else {
          navigate('/home')
        }
      }
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        toast.error("Invalid username or password")
      }
    }
  })

  return { loginUser, status, error }

}

export const useLogout = () => {

  const navigate = useNavigate()

  const { onLogout, token } = useContext<AuthContextType>(AuthContext)

  const request = async () => {
    const response = await axiosReq.post('/api/landing/logout/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  }
  const { mutate: logoutUser, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      onLogout()
      navigate('/', { replace: true })
    }
  })
  return { logoutUser, status }
}

export const useRefreshToken = () => {

  const { onLogin } = useContext<AuthContextType>(AuthContext);

  const request = async (): Promise<{
    access: string, username: string, name: string
  }> => {
    const response = await axiosReq.post('/api/landing/token/refresh/')
    return response.data
  }

  const { mutate: refreshAccessToken, isSuccess, isIdle, isError, isPending: isLoading } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {
        onLogin(data.username, data.access, data.name)
        posthog.identify(
          data.name,
          { username: data.name }
        )
      }
    }
  })

  return { refreshAccessToken, isSuccess, isIdle, isError, isLoading }

}
